import type { AxiosError, AxiosResponse } from 'axios';
import { z } from 'zod';

import type { PaginatedWalletListParams } from '../queries/walletQuery';
import type { RequestType } from '../zustandStore/slices';

import HttpRepository from './httpRepository';
import type { BillingAddress } from './paymentRepository';
export type Card = {
	cardType: string;
	cvv: string;
	id: string;
	value: number;
	balance: number;
	currency: string;
	expiryDate: string;
	expiry: string;
	validEndingOn: string;
	number: string;
	lastDigits: string;
	owner: string;
	sourceId: string;
	status: string;
	initialCardValue: number;
	request: RequestType;
	missingReceipts: number;
	provider: string;
	billingAddress: BillingAddress | undefined;
};

const WalletDetailsRequesterSchema = z.object({
	email: z.union([z.null(), z.string()]).optional(),
	firstName: z.union([z.null(), z.string()]).optional(),
	lastName: z.union([z.null(), z.string()]).optional(),
});
export type WalletDetailsRequester = z.infer<typeof WalletDetailsRequesterSchema>;

const WalletDetailsRequestSchema = z.object({
	amount: z.union([z.number(), z.null()]).optional(),
	approver: z.union([z.null(), z.string()]).optional(),
	businessId: z.union([z.null(), z.string()]).optional(),
	cardType: z.union([z.null(), z.string()]).optional(),
	currency: z.union([z.null(), z.string()]).optional(),
	isArchived: z.union([z.boolean(), z.null()]).optional(),
	lastEdit: z.union([z.coerce.date(), z.null()]).optional(),
	profileId: z.union([z.null(), z.string()]).optional(),
	authPaddingPercentage: z.union([z.number(), z.null()]).optional(),
	requestId: z.union([z.null(), z.string()]).optional(),
	state: z.union([z.null(), z.string()]).optional(),
	teamId: z.union([z.null(), z.string()]).optional(),
	title: z.union([z.null(), z.string()]).optional(),
	virtualCardId: z.union([z.null(), z.string()]).optional(),
	requester: z.union([WalletDetailsRequesterSchema, z.null()]).optional(),
});

const ApproverClassSchema = z.object({});

const WalletDetailsFieldsSchema = z.object({
	amount: z.union([z.null(), z.string()]).optional(),
	description: z.union([z.null(), z.string()]).optional(),
	endDate: z.union([z.null(), z.string()]).optional(),
	numberOfPayments: z.union([z.null(), z.string()]).optional(),
	ends: z.union([z.null(), z.string()]).optional(),
	frequency: z.union([z.null(), z.string()]).optional(),
	comments: z.union([z.null(), z.string()]).optional(),
});

const CostcentercodeSchema = z.object({
	listId: z.union([z.null(), z.string()]).optional(),
	listLabel: z.union([z.null(), z.string()]).optional(),
});

const CodingSchema = z.object({
	costcentercode: z.union([CostcentercodeSchema, z.null()]).optional(),
});

const ContextRequestSchema = z.object({
	amount: z.union([z.null(), z.string()]).optional(),
	approver: z.union([ApproverClassSchema, z.null()]).optional(),
	attachments: z.union([z.array(z.any()), z.null()]).optional(),
	authPaddingPercentage: z.union([z.number(), z.null()]).optional(),
	businessId: z.union([z.null(), z.string()]).optional(),
	cardType: z.union([z.null(), z.string()]).optional(),
	coding: z.union([CodingSchema, z.null()]).optional(),
	companyId: z.union([z.null(), z.string()]).optional(),
	currency: z.union([z.null(), z.string()]).optional(),
	fields: z.union([WalletDetailsFieldsSchema, z.null()]).optional(),
	isArchived: z.union([z.boolean(), z.null()]).optional(),
	lastEdit: z.union([z.coerce.date(), z.null()]).optional(),
	profileId: z.union([z.null(), z.string()]).optional(),
	requester: z.union([WalletDetailsRequesterSchema, z.null()]).optional(),
	requestId: z.union([z.null(), z.string()]).optional(),
	state: z.union([z.null(), z.string()]).optional(),
	teamId: z.union([z.null(), z.string()]).optional(),
	title: z.union([z.null(), z.string()]).optional(),
	virtualCardId: z.union([z.null(), z.string()]).optional(),
});

const ContextSchema = z.object({
	request: z.union([ContextRequestSchema, z.null()]).optional(),
});

const _WalletDetailsSchema = z.object({
	assignedOn: z.union([z.number(), z.null()]).optional(),
	assignees: z.union([z.array(z.string()), z.null()]).optional(),
	assigner: z.union([z.null(), z.string()]).optional(),
	context: z.union([ContextSchema, z.null()]).optional(),
	id: z.union([z.null(), z.string()]).optional(),
	state: z.union([z.null(), z.string()]).optional(),
	subjectId: z.string(),
	request: z.union([WalletDetailsRequestSchema, z.null()]).optional(),
});
export type WalletDetailsApiResponseType = z.infer<typeof _WalletDetailsSchema>;

const RequestSchema = z.object({
	amount: z.union([z.null(), z.string()]).optional(),
	authPaddingPercentage: z.union([z.number(), z.null()]).optional(),
	businessId: z.union([z.null(), z.string()]).optional(),
	cardType: z.union([z.null(), z.string()]).optional(),
	coding: z.union([CodingSchema, z.null()]).optional(),
	companyId: z.union([z.null(), z.string()]).optional(),
	currency: z.union([z.null(), z.string()]).optional(),
	isArchived: z.union([z.boolean(), z.null()]).optional(),
	lastEdit: z.union([z.coerce.date(), z.null()]).optional(),
	profileId: z.union([z.null(), z.string()]).optional(),
	requestId: z.union([z.null(), z.string()]).optional(),
	state: z.union([z.null(), z.string()]).optional(),
	teamId: z.union([z.null(), z.string()]).optional(),
	title: z.union([z.null(), z.string()]).optional(),
	virtualCardId: z.union([z.null(), z.string()]).optional(),
	fields: z.union([WalletDetailsFieldsSchema, z.null()]).optional(),
});

const CardSchema = z.object({
	cardType: z.union([z.null(), z.string()]).optional(),
	cvv: z.union([z.null(), z.string()]).optional(),
	id: z.union([z.null(), z.string()]).optional(),
	value: z.union([z.number(), z.null()]).optional(),
	balance: z.union([z.number(), z.null()]).optional(),
	currency: z.union([z.null(), z.string()]).optional(),
	expiryDate: z.union([z.null(), z.string()]).optional(),
	expiry: z.union([z.null(), z.string()]).optional(),
	validEndingOn: z.union([z.null(), z.string()]).optional(),
	number: z.union([z.null(), z.string()]).optional(),
	owner: z.union([z.null(), z.string()]).optional(),
	sourceId: z.union([z.null(), z.string()]).optional(),
	status: z.union([z.null(), z.string()]).optional(),
	initialCardValue: z.union([z.number(), z.null()]).optional(),
	lastDigits: z.union([z.null(), z.string()]).optional(),
	createdAt: z.string().optional(),
	provisioner: z.string().optional(),
	teamId: z.string().optional(),
	provider: z.union([z.literal('AMEX'), z.literal('HSBC'), z.null()]).optional(),
	billingAddress: z
		.object({
			addressLine1: z.string(),
			city: z.string(),
			countryCode: z.string(),
			region: z.string(),
			zipCode: z.string(),
		})
		.optional(),
});

const TaskSchema = z.object({
	id: z.union([z.null(), z.string()]).optional(),
	handler: z.union([z.null(), z.string()]).optional(),
	groupId: z.union([z.null(), z.string()]).optional(),
	state: z.union([z.null(), z.string()]).optional(),
	createdOn: z.union([z.number(), z.null()]).optional(),
	subjectId: z.union([z.null(), z.string()]).optional(),
	context: z.union([ContextSchema, z.null()]).optional(),
	assigner: z.union([z.null(), z.string()]).optional(),
	assignedOn: z.union([z.number(), z.null()]).optional(),
	assignees: z.union([z.array(z.string()), z.null()]).optional(),
	request: z.union([RequestSchema, z.null()]).optional(),
	card: z.union([CardSchema, z.null()]).optional(),
});

export const PaginatedWalletListResponseSchema = z.object({
	count: z.number(),
	tasks: z.union([z.array(TaskSchema), z.null()]).optional(),
	hasNextPage: z.boolean().optional(),
	// we need this, so we do not disable the next pager when we have null values in tasks
	initialPaginatedWalletDataCount: z.number(),
});

export type PaginatedWalletListResponseSchemaType = z.infer<
	Omit<typeof PaginatedWalletListResponseSchema, 'initialPaginatedWalletDataCount'>
>;

export const WalletRepository = {
	getUsersListForActiveTeam: async ({ teamId, userId }: { teamId: string; userId: string }) => {
		const params = {
			teamId,
			owner: userId,
			limit: 50,
			page: 0,
		};
		const response: AxiosResponse<PaginatedWalletListResponseSchemaType, AxiosError> =
			await HttpRepository.tradeshiftGo().get('/rest/v2/wallet', {
				params,
				timeout: 5000,
			});
		return response.data;
	},

	getWalletDetails: async ({ taskId }) => {
		const response: AxiosResponse<WalletDetailsApiResponseType, AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/rest/wallet/${taskId}`,
		);
		return response.data;
	},

	getPaginatedWalletList: async ({
		limit,
		page,
		teamId,
		owner,
		status,
		sortBy,
		sortOrder,
	}: Partial<PaginatedWalletListParams>) => {
		const params = {
			limit: limit || 10,
			page: page || 0,
			...(teamId ? { teamId } : {}),
			...(owner ? { owner } : {}),
			status,
			sortBy,
			sortOrder,
		};
		const response: AxiosResponse<
			Omit<PaginatedWalletListResponseSchemaType, 'initialPaginatedWalletDataCount'>,
			AxiosError
		> = await HttpRepository.tradeshiftGo().get('/rest/v2/wallet', {
			params,
			timeout: 5000,
		});

		return response.data;
	},

	cancelVirtualCard: async ({ subjectId, virtualCardId }) => {
		const response = await HttpRepository.tradeshiftGo().post(`/rest/tasks/${subjectId}/cards/${virtualCardId}`);
		return response.data;
	},

	getVirtualCardTask: async options => {
		const response = await HttpRepository.tradeshiftGo().get(`/rest/tasks/card/${options.virtualCardId}`);
		return response.data;
	},
};
